import { useCallback, useEffect, useState } from 'react'
import type { FilterLibrary, ReportedPicture, ReportsView, TrashedPicture } from '@shared/services'
import { changeReportTags, keepReported, readReports, readTrash, restorePictures, trashPictures } from '@shared/services'
import { useRemembered } from '../useRemembered'
import './autoFilter.css'

/**
 * Reported pictures and the Trash (Huanrui, 2026-10-08), moved out of the
 * Auto Filter page into the Review Queue (ReviewQueuePage.tsx, 2026-10-15)
 * unchanged, and shared with the Egnyte Inbox page (the Trash).
 */

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

/* -------------------------------------------------------------------------
   Reported pictures (Huanrui, 2026-10-08)
   ------------------------------------------------------------------------- */

/**
 * `plain` (the Review Queue's columns, 2026-10-15): no fold of its own, a
 * small heading instead, and nothing at all while the library has nothing
 * reported. `onCount` hears how many there are.
 */
export function ReportedSection({
  library,
  label = 'Reported',
  plain = false,
  onCount,
}: {
  library: FilterLibrary
  label?: string
  plain?: boolean
  onCount?: (count: number) => void
}) {
  const [view, setView] = useState<ReportsView | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useRemembered<boolean>(`ww.autoFilter.reported.${library}`, true)

  const load = useCallback(() => {
    readReports(library)
      .then((found) => {
        setView(found)
        setError('')
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the reports'))
  }, [library])
  useEffect(load, [load])

  // While Auto Filter is still tagging a reported picture again, look again in a while.
  const retagging = view?.reports.some((one) => one.retagging) ?? false
  useEffect(() => {
    if (!retagging) return
    const timer = window.setTimeout(load, 8000)
    return () => window.clearTimeout(timer)
  }, [retagging, load, view])

  const drop = (assetId: string) =>
    setView((now) => (now ? { ...now, total: Math.max(0, now.total - 1), reports: now.reports.filter((one) => one.assetId !== assetId) } : now))

  /*
   * MANY AT ONCE (Huanrui, 2026-10-09: one by one was too slow). A box on
   * each row, "Select all" for every one shown, then Trash or Keep for the
   * lot. Trash asks once, like the row's own button.
   */
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<'' | 'trash' | 'keep'>('')
  const [askTrash, setAskTrash] = useState(false)
  const shown = view?.reports.map((one) => one.assetId) ?? []
  const chosen = shown.filter((id) => picked.has(id))
  const allPicked = shown.length > 0 && chosen.length === shown.length
  const pick = (assetId: string, on: boolean) =>
    setPicked((now) => {
      const next = new Set(now)
      if (on) next.add(assetId)
      else next.delete(assetId)
      return next
    })
  const many = async (kind: 'trash' | 'keep') => {
    if (chosen.length === 0) return
    setBusy(kind)
    setError('')
    try {
      if (kind === 'trash') {
        await trashPictures(library, chosen)
        // The Trash section below lists them at once.
        window.dispatchEvent(new Event('ww:trash-changed'))
      } else {
        // One at a time, a few together: each is its own small request.
        let next = 0
        const lane = async () => {
          while (next < chosen.length) await keepReported(library, chosen[next++]!)
        }
        await Promise.all([lane(), lane(), lane(), lane()])
      }
      const gone = new Set(chosen)
      setView((now) =>
        now ? { ...now, total: Math.max(0, now.total - gone.size), reports: now.reports.filter((one) => !gone.has(one.assetId)) } : now,
      )
      setPicked(new Set())
      // The next ones, if more were waiting than were shown.
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work')
      load()
    } finally {
      setBusy('')
      setAskTrash(false)
    }
  }

  const total = view?.total
  useEffect(() => {
    if (total !== undefined) onCount?.(total)
  }, [total, onCount])

  if (plain && view && view.reports.length === 0 && !error) return null
  const Wrap = plain ? PlainGroup : FoldCard
  return (
    <Wrap
      className="afl__reported"
      open={open}
      onToggle={setOpen}
      head={
        <>
          {label} · {view ? view.total.toLocaleString() : '…'}
          {retagging ? <em className="afl__summary-note">tagging again…</em> : null}
        </>
      }
    >
      {error ? <p className="afl__error">{error}</p> : null}
      {view && view.reports.length === 0 ? <p className="afl__quiet">Nothing reported. Pictures flagged with the red ! show here.</p> : null}
      {view && view.reports.length > 0 ? (
        <div className="afl__bulk">
          <label className="afl__check">
            <input
              type="checkbox"
              checked={allPicked}
              disabled={Boolean(busy)}
              onChange={(event) => setPicked(event.target.checked ? new Set(shown) : new Set())}
            />
            Select all {shown.length}
          </label>
          <span className="afl__bulk-count">{chosen.length > 0 ? `${chosen.length} selected` : ''}</span>
          <span className="afl__spacer" />
          <button type="button" className="afl__mini" disabled={chosen.length === 0 || Boolean(busy)} onClick={() => void many('keep')}>
            {busy === 'keep' ? 'Keeping…' : 'Keep selected'}
          </button>
          <button
            type="button"
            className={`afl__mini afl__mini--warn${askTrash ? ' afl__mini--asking' : ''}`}
            disabled={chosen.length === 0 || Boolean(busy)}
            onClick={() => {
              if (!askTrash) {
                setAskTrash(true)
                window.setTimeout(() => setAskTrash(false), 4000)
                return
              }
              void many('trash')
            }}
          >
            {busy === 'trash' ? 'Moving…' : askTrash ? `Move ${chosen.length} to trash? Click again` : 'Trash selected'}
          </button>
        </div>
      ) : null}
      {view ? (
        <div className="afl__reports">
          {view.reports.map((one) => (
            <ReportRow
              key={one.assetId}
              library={library}
              report={one}
              view={view}
              picked={picked.has(one.assetId)}
              onPick={(on) => pick(one.assetId, on)}
              onChanged={load}
              onGone={() => drop(one.assetId)}
            />
          ))}
          {view.total > view.reports.length ? (
            <p className="afl__quiet">The first {view.reports.length} of {view.total} are shown; the rest follow as these are dealt with.</p>
          ) : null}
        </div>
      ) : null}
    </Wrap>
  )
}

/** The fold the Auto Filter page always used. */
function FoldCard({
  className,
  open,
  onToggle,
  head,
  children,
}: {
  className?: string
  open?: boolean
  onToggle?: (open: boolean) => void
  head: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <details className={`afl__card afl__fold ${className ?? ''}`} open={open} onToggle={(event) => onToggle?.(event.currentTarget.open)}>
      <summary className="afl__label">{head}</summary>
      {children}
    </details>
  )
}

/** A library's part of a Review Queue column: a small heading, then the pictures. */
export function PlainGroup({ className, head, children }: { className?: string; open?: boolean; onToggle?: (open: boolean) => void; head: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className={`rvq__group ${className ?? ''}`}>
      <div className="rvq__group-head">{head}</div>
      {children}
    </div>
  )
}

function ReportRow({
  library,
  report,
  view,
  picked,
  onPick,
  onChanged,
  onGone,
}: {
  library: FilterLibrary
  report: ReportedPicture
  view: ReportsView
  picked: boolean
  onPick: (on: boolean) => void
  onChanged: () => void
  onGone: () => void
}) {
  const [tags, setTags] = useState(report.tags)
  useEffect(() => setTags(report.tags), [report.tags])
  const [working, setWorking] = useState(false)
  const [asking, setAsking] = useState(false)
  const [error, setError] = useState('')

  const act = (job: () => Promise<unknown>, after: () => void) => {
    setWorking(true)
    setError('')
    job()
      .then(after)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'That did not work'))
      .finally(() => setWorking(false))
  }

  const removeTag = (id: string) => {
    const before = tags
    setTags((now) => now.filter((tag) => tag.id !== id))
    setWorking(true)
    changeReportTags(library, report.assetId, { remove: [id] })
      .catch((err: unknown) => {
        setTags(before)
        setError(err instanceof Error ? err.message : 'The tag could not be taken off')
      })
      .finally(() => setWorking(false))
  }
  const addTag = (id: string) => {
    const tag = view.tags.find((one) => one.id === id)
    if (!tag) return
    const before = tags
    setTags((now) => [...now, { id, name: tag.name, group: tag.group, source: 'manual', pending: false }])
    setWorking(true)
    changeReportTags(library, report.assetId, { add: [id] })
      .catch((err: unknown) => {
        setTags(before)
        setError(err instanceof Error ? err.message : 'The tag could not be added')
      })
      .finally(() => setWorking(false))
  }

  const onIt = new Set(tags.map((tag) => tag.id))
  const known = new Set(view.groups.map((group) => group.slug))
  // Tags in a category the page does not list (a hidden one) are shown as they are.
  const loose = tags.filter((tag) => !known.has(tag.group))

  return (
    <div className="afl__report">
      <div className="afl__thumb afl__thumb--big">{report.thumb ? <img src={report.thumb} alt={report.title} loading="lazy" /> : null}</div>
      <div className="afl__report-body">
        <div className="afl__report-head">
          <input
            type="checkbox"
            className="afl__pick"
            checked={picked}
            onChange={(event) => onPick(event.target.checked)}
            aria-label="Select this picture"
          />
          <span>
            Reported by {report.reportedBy.join(', ')} · {when(report.reportedAt)}
            {report.retagging ? ' · Auto Filter is tagging it again…' : ''}
          </span>
          <span className="afl__spacer" />
          <button type="button" className="afl__mini" disabled={working} onClick={() => act(() => keepReported(library, report.assetId), onGone)}>
            Keep
          </button>
          <button
            type="button"
            className={`afl__mini afl__mini--warn${asking ? ' afl__mini--asking' : ''}`}
            disabled={working}
            onClick={() => {
              if (!asking) {
                setAsking(true)
                window.setTimeout(() => setAsking(false), 4000)
                return
              }
              act(
                () => trashPictures(library, [report.assetId]),
                () => {
                  window.dispatchEvent(new Event('ww:trash-changed'))
                  onGone()
                },
              )
            }}
          >
            {asking ? 'Move to trash? Click again' : 'Trash'}
          </button>
        </div>
        <div className="afl__kinds">
          {view.groups.map((group) => {
            const here = tags.filter((tag) => tag.group === group.slug)
            const choices = view.tags.filter((tag) => tag.group === group.slug && !onIt.has(tag.id))
            return (
              <div key={group.slug} className="afl__kind">
                <span className="afl__kind-name">{group.label}</span>
                {here.map((tag) => (
                  <TagChip key={tag.id} tag={tag} disabled={working} onRemove={() => removeTag(tag.id)} />
                ))}
                {choices.length > 0 ? (
                  <select
                    className="afl__add"
                    value=""
                    disabled={working}
                    onChange={(event) => event.target.value && addTag(event.target.value)}
                    aria-label={`Add a ${group.label} tag`}
                  >
                    <option value="">+ Add</option>
                    {choices.map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            )
          })}
          {loose.length > 0 ? (
            <div className="afl__kind">
              <span className="afl__kind-name">Other</span>
              {loose.map((tag) => (
                <TagChip key={tag.id} tag={tag} disabled={working} onRemove={() => removeTag(tag.id)} />
              ))}
            </div>
          ) : null}
        </div>
        {error ? <p className="afl__error">{error}</p> : null}
      </div>
    </div>
  )
}

function TagChip({ tag, disabled, onRemove }: { tag: ReportedPicture['tags'][number]; disabled: boolean; onRemove: () => void }) {
  return (
    <span
      className={`afl__chip afl__chip--tag${tag.source === 'ai' ? ' afl__chip--ai' : ''}${tag.pending ? ' afl__chip--new' : ''}`}
      title={tag.source === 'ai' ? 'Put on by Auto Filter' : 'Put on by hand'}
    >
      {tag.name}
      {tag.source === 'ai' ? <em>AI</em> : null}
      <button type="button" className="afl__chip-x" disabled={disabled} onClick={onRemove} aria-label={`Take ${tag.name} off`}>
        ×
      </button>
    </span>
  )
}

/* -------------------------------------------------------------------------
   The trash (Huanrui, 2026-10-08)
   ------------------------------------------------------------------------- */

export function TrashSection({
  library,
  label = 'Trash',
  plain = false,
  onCount,
}: {
  library: FilterLibrary | 'egnyte'
  label?: string
  /** As ReportedSection's. */
  plain?: boolean
  onCount?: (count: number) => void
}) {
  const [pictures, setPictures] = useState<TrashedPicture[] | null>(null)
  const [days, setDays] = useState(30)
  const [error, setError] = useState('')
  const [working, setWorking] = useState('')

  const load = useCallback(() => {
    readTrash(library)
      .then((found) => {
        setPictures(found.pictures)
        setDays(found.days)
        setError('')
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the trash'))
  }, [library])
  useEffect(load, [load])
  // Something moved to the trash elsewhere on the page: look again.
  useEffect(() => {
    const again = () => load()
    window.addEventListener('ww:trash-changed', again)
    return () => window.removeEventListener('ww:trash-changed', again)
  }, [load])

  const restore = (assetId: string) => {
    setWorking(assetId)
    restorePictures(library, [assetId])
      .then(() => setPictures((now) => (now ? now.filter((one) => one.assetId !== assetId) : now)))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'It could not be restored'))
      .finally(() => setWorking(''))
  }

  const count = pictures?.length
  useEffect(() => {
    if (count !== undefined) onCount?.(count)
  }, [count, onCount])

  if (plain && pictures && pictures.length === 0 && !error) return null
  const Wrap = plain ? PlainGroup : FoldCard
  return (
    <Wrap
      open={plain ? true : undefined}
      head={
        <>
          {label} · {pictures ? pictures.length.toLocaleString() : '…'}
          {plain ? null : <em className="afl__summary-note">deleted for good {days} days after they were put there</em>}
        </>
      }
    >
      {error ? <p className="afl__error">{error}</p> : null}
      {pictures && pictures.length === 0 ? <p className="afl__quiet">The trash is empty.</p> : null}
      {pictures && pictures.length > 0 ? (
        <div className="afl__trash">
          {pictures.map((one) => (
            <div key={one.assetId} className="afl__trashed">
              <div className="afl__thumb afl__thumb--fill">{one.thumb ? <img src={one.thumb} alt={one.title} loading="lazy" /> : null}</div>
              <span className="afl__trashed-meta">
                {one.trashedBy} · {day(one.trashedAt)}
                <br />
                gone {day(one.goneAt)}
              </span>
              <button type="button" className="afl__mini" disabled={working === one.assetId} onClick={() => restore(one.assetId)}>
                {working === one.assetId ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </Wrap>
  )
}
